/* eslint-disable */
// Sample schedule generator — ported from the prototype `withSampleSchedules`
// (dros-beta-prototype_524.html ~28138–28278). It books crews onto active
// projects across a today-relative window (−5/+7 months) and logs a clock-in/out
// time entry per attended crew member on recent past shifts (with geo flags, work
// notes, and approvals). Work sessions and time entries were never static seed
// data in the prototype — they are produced here, exactly as the prototype did,
// so Time Tracking and the Scheduling views populate with realistic data.
//
// Kept deliberately close to the original (loose typing, verbatim logic) so it
// stays faithful and easy to diff against the source.

import { Project } from '../../models';
import { DEFAULT_CATALOG } from '../prototype/project-settings-data';

/** Number of CPM catalog activities — a project progresses through them over its weeks. */
const CAT_LEN = Math.max(1, DEFAULT_CATALOG.length);
/** Map a week index to a CPM activity index (early weeks → early activities, capped). */
const actIdxFor = (wk: number) => Math.min(CAT_LEN - 1, wk);

interface PersonaLite {
  id: string;
  name: string;
}

export function withSampleSchedules(list: Project[], personas: PersonaLite[]): Project[] {
  const nameById: Record<string, string> = {};
  personas.forEach((p) => (nameById[p.id] = p.name));
  const has = (id: string) => id in nameById;

  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const parse = (s: any) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + 'T00:00:00') : null);
  let n = 0;
  const sid = () => 'wss' + ++n;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const winStart = new Date(today);
  winStart.setMonth(winStart.getMonth() - 5);
  const winEnd = new Date(today);
  winEnd.setMonth(winEnd.getMonth() + 7);
  const shifts = [['08:00', '15:30'], ['08:00', '15:30'], ['08:00', '15:00']];
  const notes = ['Framing & layout', 'Concrete & rebar', 'MEP rough-in', 'Sitework & grading', 'Exterior finishes', 'Deck pour', 'Drywall & finishes', 'Inspection & punch'];
  const active = list.filter((pr: any) => !/complete/i.test(pr.status || pr.state || '') && pr.mode !== 'dispatch' && pr.osId !== 'PMOS');
  const nA = active.length;
  const sessions: Record<string, any[]> = {};
  list.forEach((pr) => (sessions[pr.id] = []));
  if (!nA) return list;
  const BENCH = ['joe', 'sarah', 'bill'];
  const people = personas.map((p) => p.id).filter((id) => !BENCH.includes(id));
  const coversToday = (pr: any) => {
    const ps = parse(pr.start),
      pf = parse(pr.end);
    return (!ps || ps <= today) && (!pf || pf >= today);
  };
  const pool = (() => {
    const nw = active.filter(coversToday);
    return nw.length ? nw : active;
  })();
  const nP = pool.length;
  const idxOf = (pr: any) => Math.max(0, pool.indexOf(pr));
  const crewA = people.slice(0, 6);
  const crewB = people.slice(6, 9);
  const pA = pool[0];
  const pC = pool[Math.min(2, nP - 1)];
  const splitPr = pool.find((p) => p.id !== (pA && pA.id) && p.id !== (pC && pC.id)) || pool[Math.min(1, nP - 1)] || pA;

  const book = (pr: any, crew: string[], weekdays: number[], shift: string[]) => {
    if (!pr || !crew.length) return;
    const ps = parse(pr.start),
      pf = parse(pr.end);
    const base = new Date(Math.max((ps || winStart).getTime(), winStart.getTime()));
    const cap = new Date(Math.min((pf || winEnd).getTime(), winEnd.getTime()));
    if (cap <= base) return;
    const d = new Date(base);
    let wk = 0;
    while (d <= cap) {
      weekdays.forEach((wd) => {
        const day = new Date(d);
        day.setDate(day.getDate() + ((wd - day.getDay() + 7) % 7));
        if (day < base || day > cap) return;
        sessions[pr.id].push({ id: sid(), date: iso(day), start: shift[0], end: shift[1], crew: [...crew], notes: notes[(idxOf(pr) + wk + wd) % notes.length], activityIdx: actIdxFor(wk) });
      });
      d.setDate(d.getDate() + 7);
      wk++;
    }
  };

  book(pA, crewA, [1, 2], shifts[0]);
  book(pC || pA, crewA, [3, 4], shifts[1]);
  if (pA && has('joe')) book(pA, ['joe'], [1, 2, 3, 4, 5], ['08:00', '17:00']);

  const shiftSets = [
    [['08:00', '11:30'], ['12:00', '15:30']],
    [['08:00', '10:30'], ['11:00', '13:00'], ['13:30', '15:30']],
  ];
  if (splitPr) {
    const ps = parse(splitPr.start),
      pf = parse(splitPr.end);
    const base = new Date(Math.max((ps || winStart).getTime(), winStart.getTime()));
    const cap = new Date(Math.min((pf || winEnd).getTime(), winEnd.getTime()));
    if (cap > base) {
      const d = new Date(base);
      let wk = 0;
      while (d <= cap) {
        [1, 2, 3, 4].forEach((wd, wi) => {
          const day = new Date(d);
          day.setDate(day.getDate() + ((wd - day.getDay() + 7) % 7));
          if (day < base || day > cap) return;
          const set = shiftSets[(wi + wk) % shiftSets.length];
          set.forEach(([st, en], si) => sessions[splitPr.id].push({ id: sid(), date: iso(day), start: st, end: en, crew: [...crewB], notes: `Split shift ${si + 1} of ${set.length}`, activityIdx: actIdxFor(wk) }));
        });
        d.setDate(d.getDate() + 7);
        wk++;
      }
    }
  }

  const near = (off: number) => {
    const x = new Date(today);
    x.setDate(x.getDate() + off);
    if (x.getDay() === 0) x.setDate(x.getDate() + 1);
    if (x.getDay() === 6) x.setDate(x.getDate() + 2);
    return x;
  };
  if (splitPr)
    [3, 4, 10, 11].forEach((off, i) => {
      const person = crewA[i % crewA.length];
      const dt = iso(near(off));
      const target = (sessions[splitPr.id] || []).find((s) => s.date === dt);
      if (target && !(target.crew || []).includes(person)) target.crew = [...target.crew, person];
    });

  let apI = 0;
  Object.keys(sessions).forEach((pid) => {
    const arr = sessions[pid];
    if (!arr.length) return;
    const quota = Math.min(arr.length, 2 + (apI % 2));
    apI++;
    const picks: number[] = [];
    for (let q = 0; q < quota; q++) {
      const idx = Math.floor((arr.length * (q + 1)) / (quota + 1));
      if (!picks.includes(idx) && arr[idx]) picks.push(idx);
    }
    picks.forEach((idx, q) => {
      const s = arr[idx];
      if (q % 2 === 0) {
        s.start = '06:00';
        s.notes = (s.notes ? s.notes + ' · ' : '') + 'Early start';
      } else {
        s.end = '18:00';
        s.notes = (s.notes ? s.notes + ' · ' : '') + 'Late finish';
      }
    });
  });

  const compNotes = ['Poured as planned — no issues', 'On schedule, full crew', 'Short one hand, covered by others', 'City inspection passed', 'Early weather delay, recovered by noon', 'Materials delivered on time', 'Punch items cleared', 'Rough-in complete, ready for inspection'];
  const compIssues = ['Rebar delivery arrived 2 hrs late — held up the morning pour.', 'Inspector flagged two hangers that need re-strapping before cover.', 'Ran short on 3/4" conduit; borrowed from the north building.', 'Ground was wetter than expected — extra dewatering needed.'];
  const compNext = ['Schedule re-inspection once strapping is corrected.', 'Confirm concrete truck timing with the supplier for next pour.', 'Order additional conduit before Thursday.', 'Follow up with owner on the color selection for finishes.'];
  const recentCutoff = new Date(today);
  recentCutoff.setDate(recentCutoff.getDate() - 42);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 8);
  let ci2 = 0;
  const timeEntriesByPid: Record<string, any[]> = {};
  let teN = 0;
  const atTime = (dd: Date, hm: string) => {
    if (!hm) return null;
    const [h, m] = hm.split(':').map(Number);
    const x = new Date(dd);
    x.setHours(h, m, 0, 0);
    return x;
  };
  Object.keys(sessions).forEach((pid) => {
    sessions[pid].forEach((s) => {
      const dd = parse(s.date);
      if (!dd || dd >= today || dd < recentCutoff) return;
      const crew = s.crew || [];
      const attended = crew.length > 1 && ci2 % 5 === 4 ? crew.slice(0, -1) : [...crew];
      s.completed = true;
      s.attended = attended;
      s.completionNote = compNotes[ci2 % compNotes.length];
      {
        const loggers = [['ben', 'Ben Hull'], ['chris', 'Chris Hardy']];
        const lg = loggers[ci2 % loggers.length];
        s.completedBy = lg[0];
        s.completedByName = lg[1];
      }
      if (ci2 % 4 === 1) s.completionIssues = compIssues[((ci2 / 4) | 0) % compIssues.length];
      if (ci2 % 4 === 2) s.completionNextSteps = compNext[((ci2 / 4) | 0) % compNext.length];
      s.completedAt = new Date(dd.getTime() + 16 * 3600e3).toISOString();
      if (s.start && s.end) {
        attended.forEach((id: string) => {
          if (!has(id)) return;
          const inD = atTime(dd, s.start),
            outD = atTime(dd, s.end);
          if (!inD || !outD || outD <= inD) return;
          const ciOff = [-6, 0, 4, 9][teN % 4],
            coOff = [0, -5, 8, 14][teN % 4];
          const ci3 = new Date(inD.getTime() + ciOff * 60000);
          const co3 = new Date(outD.getTime() + coOff * 60000);
          const hrs = Math.round(Math.max(0, (co3.getTime() - ci3.getTime()) / 3600000) * 10) / 10;
          let geo;
          if (teN % 8 === 3) geo = { status: 'off-site', dist: 280 + (teN % 5) * 70, notes: ['Parked across the lot', 'GPS drift near the trailer', 'Ran to the supply yard'][teN % 3] };
          else if (teN % 13 === 6) geo = { status: 'unverified', notes: 'Location permission off' };
          else geo = { status: 'on-site', dist: 8 + (teN % 6) * 6 };
          const workNotes = ['Formed and poured footings on the east side.', 'Framed interior walls, units 3–5.', 'Ran electrical rough-in, second floor.', 'Set trusses and sheathed the roof.', 'Hung and taped drywall in the lobby.', 'Grading and site prep around the pad.', 'Punch-list items and touch-up paint.', 'Loaded out and staged material for tomorrow.'];
          const appr = dd < weekAgo && teN % 6 !== 0;
          (timeEntriesByPid[pid] = timeEntriesByPid[pid] || []).push({
            id: 'teseed_' + s.id + '_' + id,
            personaId: id,
            personaName: nameById[id] || id,
            date: s.date,
            clockIn: ci3.toISOString(),
            clockOut: co3.toISOString(),
            hours: hrs,
            workNote: teN % 3 === 2 ? '' : workNotes[teN % workNotes.length],
            geo,
            // reviewStatus is what the Time Tracking screen buckets on; keep it in
            // sync with the prototype's `approved` flag.
            reviewStatus: appr ? 'approved' : 'pending',
            ...(appr ? { approved: true, approvedBy: 'ben', approvedByName: 'Ben Hull', approvedAt: new Date(dd.getTime() + 40 * 3600e3).toISOString() } : { approved: false }),
          });
          teN++;
        });
      }
      ci2++;
    });
  });

  for (const pid of Object.keys(sessions)) {
    const fut = (sessions[pid] || []).filter((s) => {
      const dd = parse(s.date);
      return dd && dd > today && (s.crew || []).length;
    }).sort((a, b) => a.date.localeCompare(b.date));
    if (fut.length) {
      const s = fut[0];
      const by = s.crew[0];
      s.changeReq = { by, byName: nameById[by] || by, notes: 'Have a morning appointment — can I start at 9 instead of 8?', at: new Date().toISOString(), status: 'pending' };
      break;
    }
  }

  return list.map((pr) => {
    const ws = sessions[pr.id];
    const te = timeEntriesByPid[pr.id];
    if ((!ws || !ws.length) && (!te || !te.length)) return pr;
    return { ...pr, ...(ws && ws.length ? { workSessions: ws } : {}), ...(te && te.length ? { timeEntries: te } : {}) };
  });
}
